import { template } from "@ember/template-compiler";
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { classify } from '@ember/string';
import Component from '@glimmer/component';
import type { EmberChangeset } from 'ember-changeset';
import { Changeset } from 'ember-changeset';
import { and } from 'ember-truth-helpers';
import type { ModelListItem } from 'parog-web/components/model-list';
import type FollowUpReason from 'parog-web/models/follow-up-reason';
import type { TreatmentProtocol } from 'parog-web/models/protocol';
import Protocol from 'parog-web/models/protocol';
import * as ProtocolSections from './protocol-sections';
interface Args {
    /** Changeset containing a TreatmentHistoryChangedJournalEvent */ changeset: EmberChangeset;
    followUpReasonId: FollowUpReason['id'];
    item: ModelListItem;
}
export interface ProtocolSectionArgs {
    /** Local Changeset containing the protocol */ changeset: EmberChangeset;
    item: ModelListItem;
    followUpReasonId: FollowUpReason['id'];
    protocolModel: Protocol;
    preTreatmentSynthesisRoute?: string;
    resumeProtocol?: () => void;
    onChangesetUpdate: (changeset: EmberChangeset) => void;
}
export default class TreatmentHistoryEditionFormProtocolsViewBaseProtocolItemComponent extends Component<Args> {
    @service
    router: RouterService;
    /**
   * The local changeset created for this specific item.
   * It should be executed and then set as dirty to propagate changes into the main changeset
   **/ localChangeset: EmberChangeset;
    private protocolModel: Protocol;
    constructor(owner1: unknown, args1: Args){
        super(owner1, args1);
        this.localChangeset = Changeset(args1.item.item as object);
        this.protocolModel = Protocol.new(this.localChangeset.data);
    }
    get protocolComponent() {
        return ProtocolSections[classify(this.protocol)];
    }
    get preTreatmentSynthesisRoute(): string | undefined {
        if (this.patientId && this.treatmentId && this.protocolUuid) {
            return this.router.urlFor('medics-web.protected.patients.patient.pre-treatment-synthesis', this.patientId, {
                queryParams: {
                    treatmentId: this.treatmentId,
                    protocolUuid: this.protocolUuid
                }
            });
        }
        return undefined;
    }
    get hasProtocol(): boolean {
        return Boolean(this.protocol);
    }
    get patientId(): string {
        return this.args.changeset.get('patient')?.id;
    }
    get treatmentId(): string | undefined {
        return this.args.changeset.get('id');
    }
    get protocolUuid(): string {
        return this.localChangeset.get('uuid');
    }
    get protocol(): TreatmentProtocol {
        return this.localChangeset.get('protocol');
    }
    get protocols() {
        return this.args.changeset.get('protocols');
    }
    @action
    onChangesetUpdate(changeset1: EmberChangeset) {
        changeset1.execute();
        this.setAsDirty();
    }
    @action
    resumeProtocol() {
        this.localChangeset.set('pause-date', null);
        this.localChangeset.set('pause-time-frequency-value', null);
        this.localChangeset.set('pause-time-frequency-unit', null);
        this.localChangeset.set('paused', false);
        this.localChangeset.execute();
        this.setAsDirty();
    }
    setAsDirty() {
        this.args.changeset.set('protocols', [].concat(this.protocols));
    }
    static{
        template(`
    <div class='mb-6'>
      {{#if (and this.hasProtocol this.protocolComponent)}}
        <this.protocolComponent
          @changeset={{this.localChangeset}}
          @onChangesetUpdate={{this.onChangesetUpdate}}
          @followUpReasonId={{@followUpReasonId}}
          @item={{@item}}
          @protocolModel={{this.protocolModel}}
          @preTreatmentSynthesisRoute={{this.preTreatmentSynthesisRoute}}
          @resumeProtocol={{this.resumeProtocol}}
        />
      {{/if}}
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
